import { useEffect, useState } from "react";

// Platform-console preference: include internal/demo tenants in analytics
// aggregates (ADR 0022). Excluded by default; the workspace header toggle
// flips this and every aggregate hook appends ?include_internal=true.

const PREF_KEY = "adam:platform:include_internal";
const PREF_EVENT = "adam:platform-internal-pref-changed";

export function getIncludeInternal(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === "true";
  } catch {
    return false;
  }
}

export function setIncludeInternalPref(value: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, value ? "true" : "false");
  } catch {
    // localStorage unavailable — preference is in-memory for this tab only.
  }
  try {
    window.dispatchEvent(new CustomEvent<boolean>(PREF_EVENT, { detail: value }));
  } catch {
    /* non-browser env */
  }
}

/** Append the include_internal flag to a platform query string when active. */
export function internalParam(includeInternal: boolean): string {
  return includeInternal ? "&include_internal=true" : "";
}

/**
 * Reactive preference state. Re-renders when the toggle changes in this tab
 * (custom event) or another tab (storage event).
 */
export function useInternalDataPref(): {
  includeInternal: boolean;
  setIncludeInternal: (value: boolean) => void;
} {
  const [includeInternal, setState] = useState<boolean>(() => getIncludeInternal());

  useEffect(() => {
    const sync = () => setState(getIncludeInternal());
    window.addEventListener(PREF_EVENT, sync as EventListener);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREF_EVENT, sync as EventListener);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return { includeInternal, setIncludeInternal: setIncludeInternalPref };
}
